(() => {
  const randomizedLists = new WeakSet();
  const collectedLists = new WeakSet();
  const excludedCollectionPath = '/collections/kast-samlingen';
  const maxSoldOutPages = 10;

  function hasSortParameter() {
    const params = new URLSearchParams(window.location.search);
    const sortBy = params.get('sort_by');
    return typeof sortBy === 'string' && sortBy.trim() !== '';
  }

  function shouldSkipRandomization() {
    const currentPath = window.location.pathname.toLowerCase().replace(/\/$/, '');
    return currentPath === excludedCollectionPath || hasSortParameter();
  }

  function shuffleItems(items) {
    for (let i = items.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
  }

  function randomizeList(list) {
    if (!list || randomizedLists.has(list)) return;

    const allItems = Array.from(list.children).filter((child) => child.tagName === 'LI');
    if (allItems.length < 2) return;

    // Pinned tiles (e.g. the Mystery Box) always stay first, in DOM order.
    // Sold-out tiles always stay last, also shuffled. Only the rest get
    // shuffled in between.
    const pinned = allItems.filter((li) => li.hasAttribute('data-pinned'));
    const soldOut = allItems.filter((li) => !li.hasAttribute('data-pinned') && li.hasAttribute('data-sold-out'));
    const shufflable = allItems.filter((li) => !li.hasAttribute('data-pinned') && !li.hasAttribute('data-sold-out'));
    const orderedItems = pinned.concat(shuffleItems(shufflable), shuffleItems(soldOut));
    const fragment = document.createDocumentFragment();

    orderedItems.forEach((item, index) => {
      if (item.matches('.scroll-trigger.animate--slide-in')) {
        item.style.setProperty('--animation-order', String(index + 1));
      }
      fragment.appendChild(item);
    });

    list.appendChild(fragment);
    randomizedLists.add(list);
  }

  // Earlier pages of a multi-page collection leave sold-out products out (see
  // main-collection-product-grid.liquid). The last page fetches all sold-out
  // products through the availability filter and appends the ones it does not
  // already show, so they all end up at the very bottom of the collection.
  async function collectSoldOut(list) {
    if (!list || collectedLists.has(list) || list.dataset.collectSoldOut !== 'true') return;
    collectedLists.add(list);

    const shown = new Set(
      Array.from(list.children)
        .map((li) => li.dataset.productId)
        .filter(Boolean)
    );
    const params = new URLSearchParams(window.location.search);
    params.set('filter.v.availability', '0');
    params.set('section_id', list.dataset.id);

    const found = [];
    let pages = 1;
    for (let page = 1; page <= Math.min(pages, maxSoldOutPages); page += 1) {
      params.set('page', String(page));
      let grid;
      try {
        const response = await fetch(`${window.location.pathname}?${params.toString()}`);
        if (!response.ok) break;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        grid = html.getElementById('product-grid');
      } catch (error) {
        break;
      }
      if (!grid) break;
      pages = Number(grid.dataset.pages) || 1;
      Array.from(grid.children).forEach((li) => {
        const id = li.dataset.productId;
        if (!id || shown.has(id) || !li.hasAttribute('data-sold-out') || li.hasAttribute('data-pinned')) return;
        shown.add(id);
        li.classList.add('scroll-trigger--cancel');
        found.push(document.adoptNode(li));
      });
    }

    if (!found.length || !list.isConnected) return;
    const fragment = document.createDocumentFragment();
    (shouldSkipRandomization() ? found : shuffleItems(found)).forEach((li) => fragment.appendChild(li));
    list.appendChild(fragment);
  }

  function processLists(scope = document) {
    scope.querySelectorAll('[data-randomize-products="true"]').forEach((list) => {
      if (!shouldSkipRandomization()) randomizeList(list);
      collectSoldOut(list);
    });
  }

  function startObserver() {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (!productGridContainer) return;

    const observer = new MutationObserver(() => {
      processLists(productGridContainer);
    });

    observer.observe(productGridContainer, { childList: true, subtree: true });
  }

  document.addEventListener('DOMContentLoaded', () => {
    processLists();
    startObserver();
  });
})();
